import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import {
  PublicClientApplication,
  SilentRequest,
  BrowserAuthError,
} from "@azure/msal-browser";
import { loginRequest, msalConfig } from "../config/auth-config";
import { getApiBaseUrl } from "../config/env";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getAuthClaimsEntity } from "../utils";

const { graphqlEndpoint } = getApiBaseUrl();

const uploadLink = createUploadLink({
  uri: graphqlEndpoint,
  headers: {
    "GraphQL-Preflight": "1",
    Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
  },
});

const refreshAccessToken = async (refresh_token) => {
  const tokenEnpoint = `${getApiBaseUrl().origin}/api/Identity/refresh_token`;
  try {
    const body = {
      refresh_token,
      grant_type: "refresh_token",
    };

    const response = await fetch(tokenEnpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    return response.json();
  } catch (error) {
    console.error("Token refresh error", error);
    localStorage.clear();
  }
};

const authLink = setContext(async (_, { headers }) => {
  const token = localStorage.getItem("token");
  const refresh_token = localStorage.getItem("refresh_token") as string;

  if (token) {
    const decodedToken = getAuthClaimsEntity(token);
    const currentTime = Date.now() / 1000;

    if (!decodedToken?.exp || decodedToken?.exp < currentTime) {
      const response = await refreshAccessToken(refresh_token);
      if (response) {
        localStorage.setItem("token", response.access_token);

        return {
          headers: {
            ...headers,
            authorization: `Bearer ${response.access_token}`,
          },
        };
      } else {
        return {
          headers: {
            ...headers,
          },
        };
      }
    }
  }

  const head = {
    ...headers,
    authorization: token ? `Bearer ${token}` : "",
  };

  return {
    headers: head,
  };
});

const getNewToken = async () => {
  const publicClientApplication = new PublicClientApplication(msalConfig);
  const account = publicClientApplication.getAllAccounts()[0];

  const accessTokenRequest = {
    ...loginRequest,
    account: account,
  } as SilentRequest;
  return publicClientApplication
    .acquireTokenSilent(accessTokenRequest)
    .then(function (accessTokenResponse) {
      let accessToken = accessTokenResponse.accessToken;
      localStorage.setItem("token", accessToken);
      return accessToken;
    })
    .catch(function (error) {
      if (error instanceof BrowserAuthError) {
        if (error.errorCode === "no_account_error") {
          console.error(error);
        } else {
          publicClientApplication.loginRedirect(loginRequest);
        }
      } else {
        localStorage.clear();
        publicClientApplication.logoutRedirect();
      }
    });
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      const oldHeaders = operation.getContext().headers;
      for (let err of graphQLErrors) {
        switch (err?.extensions?.code) {
          case "804":
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${getNewToken()}`,
              },
            });

            return forward(operation);
          case "404":
        }
      }
    }
  },
);

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      return !!error && operation.operationName !== "specialCase";
    },
  },
  delay: (count) => {
    return count * 10 * Math.random();
  },
});

const customPaginationMerge = (existing, incoming, obj) => {
  
  if (obj?.variables?.cursor && existing) {
    return merge();
  }

  function merge() {
    if (!incoming?.nodes || incoming?.nodes?.length < 1) return existing;
    let newObj = {
      ...existing,
      ...incoming,
      nodes: mergeByKey(existing?.nodes ?? [], incoming?.nodes ?? [], "__ref"),
    };
    return newObj;
  }

  return incoming;
};

const httplink = ApolloLink.from([authLink, errorLink, retryLink, uploadLink]);

const client = new ApolloClient({
  link: httplink,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
  cache: new InMemoryCache({
    typePolicies: {
      User: {
        keyFields: ["id", "userName"],
      },
      Query: {
        fields: {
          products: {
            keyArgs: ["id", "where"],
            merge: customPaginationMerge,
          },
          payments: {
            keyArgs: ["id", "where"],
            merge: customPaginationMerge,
          },
          backOfficePayments: {
            keyArgs: ["companyId", "locationIds", "where", "order"],
            merge: customPaginationMerge,
          },
          backOfficeSalesVouchers: {
            keyArgs: ["companyId", "locationIds", "where", "order"],
            merge: customPaginationMerge,
          },
          drivers: {
            keyArgs: ["id", "where"],
            merge: customPaginationMerge,
          },
          customers: {
            keyArgs: ["id", "where"],
            merge: customPaginationMerge,
          },
          countries: {
            keyArgs: ["id", "where"],
            merge: customPaginationMerge,
          },
          companies: {
            keyArgs: ["where", "id", "first"],
            merge: customPaginationMerge,
          },
          locationProducts: {
            keyArgs: ["id"],
            merge: customPaginationMerge,
          },
          locationProduct: {
            keyArgs: ["id"],
            merge: customPaginationMerge,
          },
          locationDestinations: {
            keyArgs: ["id"],
            merge: customPaginationMerge,
          },
          destinations: {
            keyArgs: ["id"],
            merge: customPaginationMerge,
          },
          locations: {
            keyArgs: ["id", "where", "where.companyId.in", "first"],
            merge: customPaginationMerge,
          },
          salesVouchers: {
            keyArgs: ["id", "where", "first"],
            merge: customPaginationMerge,
          },
          salesVoucherOrders: {
            keyArgs: ["id", "where", "first", "order",],
            merge: customPaginationMerge,
          },
          salesVoucherItems: {
            keyArgs: ["id", "where", "first"],
            merge: customPaginationMerge,
          },
          transitionalSalesVoucherItems: {
            keyArgs: ["id", "where", "first"],
            merge: customPaginationMerge,
          },
          dfsPayments: {
            keyArgs: ["where", "first"],
            merge: customPaginationMerge,
          },
          cheques: {
            keyArgs: ["where", "first"],
            merge: customPaginationMerge,
          },
          paymentApprovals: {
            keyArgs: ["where", "first"],
            merge: customPaginationMerge,
          },
          users: {
            keyArgs: ["email", "id", "searchString", "where"],

            merge: (existing, incoming, obj) => {
              
              // Handle pagination for both allUsers alias and getUserReport query
              if (obj.variables?.cursor && existing) {
                if (!incoming?.nodes || incoming?.nodes?.length < 1)
                  return existing;
                let newObj = {
                  ...existing,
                  ...incoming,
                  nodes: mergeByKey(
                    existing?.nodes ?? [],
                    incoming?.nodes ?? [],
                    "__ref",
                  ),
                };
                return newObj;
              }
              return incoming;
            },
          },
          usersByCompany: {
            keyArgs: ["id", "companyId", "fullName"],
            merge: customPaginationMerge,
          },
        },
      },
    },
  }),
});

const mergeByKey = (a, b, key) => {
  function x(a) {
    a.forEach(function (b) {
      if (!(b[key] in obj)) {
        obj[b[key]] = obj[b[key]] || {};
        array.push(obj[b[key]]);
      }
      Object.keys(b).forEach(function (k) {
        obj[b[key]][k] = b[k];
      });
    });
  }

  var array: any[] = [],
    obj = {};

  x(a);
  x(b);
  return array;
};

export { ApolloProvider } from "@apollo/client";
export { client };
